import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import {
  AlertTriangle, ChevronDown, Gamepad2, Globe, PiggyBank, Zap,
  Cpu, HardDrive, Wifi, Clock, Sparkles, ArrowRight,
  Check, Server, Users, Wallet, Shield,
} from 'lucide-react';
import { MarketingLayout } from '../components/MarketingLayout';
import { SEO } from '../components/SEO';
import { CTABanner } from '../components/CTABanner';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary';

// =========================================================================
// Game catalog
// =========================================================================

interface Game {
  name: string;
  image: string;
  tag: string;
  players: string;
  available: boolean;
}

const games: Game[] = [
  {
    name: 'Minecraft',
    image: '/assets/Minecraft Java.webp',
    tag: 'Java',
    players: 'Hasta 50 jugadores',
    available: true,
  },
  {
    name: 'Counter Strike 1.6',
    image: '/assets/Counter Strike 1_6.webp',
    tag: 'Competitivo & Casual',
    players: 'Hasta 32 jugadores',
    available: true,
  },
  {
    name: 'Counter Strike Source',
    image: '/assets/Counter Strike Source.webp',
    tag: 'Competitivo & Casual',
    players: 'Hasta 32 jugadores',
    available: true,
  },
  {
    name: 'Terraria',
    image: '/assets/Terraria.webp',
    tag: 'Sandbox & Aventura',
    players: 'Hasta 8 jugadores',
    available: true,
  },
  {
    name: 'Project Zomboid',
    image: '',
    tag: 'Survival',
    players: '',
    available: false,
  },
  {
    name: 'Unturned',
    image: '',
    tag: 'Survival',
    players: '',
    available: false,
  },
];

// =========================================================================
// Feature list
// =========================================================================

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  highlights: string[];
}

const features: Feature[] = [
  {
    icon: <Gamepad2 size={28} />,
    title: 'Múltiples juegos',
    description: 'Se estarán añadiendo más juegos constantemente.',
    highlights: ['Minecraft Java', 'Counter Strike 1.6 & Source', 'Terraria', 'Y muchos más próximamente...'],
  },
  {
    icon: <Cpu size={28} />,
    title: 'Alto rendimiento',
    description: 'Hardware real para jugadores reales. Sin cuellos de botella.',
    highlights: ['CPU dedicado', 'RAM exclusiva', 'SSD NVMe', 'Uptime 99.9%'],
  },
  {
    icon: <Globe size={28} />,
    title: 'Servidores globales',
    description: 'Múltiples ubicaciones para la menor latencia posible.',
    highlights: ['Europa', 'América'],
  },
  {
    icon: <PiggyBank size={28} />,
    title: 'Paga solo por jugar',
    description: 'Planes para todos los presupuestos. Sin compromisos.',
    highlights: ['Recarga mínima de $1', 'Zinli, Visa, Kontigo, Binance y PayPal', 'Sin suscripciones'],
  },
];

// =========================================================================
// Trust badges
// =========================================================================

const trustBadges = [
  { icon: <Shield size={14} />, label: 'Pagos seguros' },
  { icon: <Clock size={14} />,    label: 'Setup en 2 min' },
  { icon: <Wifi size={14} />,     label: '99.9% uptime' },
  { icon: <HardDrive size={14} />, label: 'Hardware dedicado' },
];

// =========================================================================
// Motion variants — premium but restrained
// =========================================================================

const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];
const EASE_BACK: [number, number, number, number] = [0.34, 1.56, 0.64, 1];

const heroTitle: Variants = {
  hidden: { opacity: 0, y: 60, scale: 0.92, filter: 'blur(12px)' },
  show:   { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)',
    transition: { duration: 1.1, ease: EASE_OUT } },
};

const heroItem: Variants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  show:   { opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.8, ease: EASE_OUT } },
};

const eyebrow: Variants = {
  hidden: { opacity: 0, y: 20, scale: 0.8 },
  show:   { opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.6, ease: EASE_BACK } },
};

const sectionReveal: Variants = {
  hidden: { opacity: 0, y: 60 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE_OUT } },
};

const fadeIn: Variants = {
  hidden: { opacity: 0 },
  show:   { opacity: 1, transition: { duration: 0.7, ease: EASE_OUT } },
};

const cardItem: Variants = {
  hidden: { opacity: 0, y: 50, scale: 0.92 },
  show:   { opacity: 1, y: 0, scale: 1,
    transition: { duration: 0.7, ease: EASE_OUT } },
};

const stagger: Variants = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};

const tightStagger: Variants = {
  hidden: {},
  show:   { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const glowFloat = (delay: number): Variants => ({
  hidden: { opacity: 0, scale: 0.5 },
  show:   {
    opacity: 0.5,
    scale: 1,
    transition: { duration: 1.6, delay, ease: EASE_OUT },
  },
});

// =========================================================================
// Home
// =========================================================================

export function Home() {
  const navigate = useNavigate();
  // Empezamos en `false` para que el HTML pre-renderizado muestre el
  // contenido (mejor para SEO). El efecto cliente puede redirigir a
  // /dashboard si está autenticado, sin mostrar spinner largo.
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/profile`, {
        credentials: 'include',
      });
      if (res.ok) {
        navigate('/dashboard');
        return;
      }
    } catch {
      // Not authenticated, stay on home
    }
    setLoading(false);
  };

  const handleLogin = () => {
    window.location.href = '/login';
  };

  const prefersReducedMotion = useReducedMotion();

  const handleScrollToFeatures = useCallback(() => {
    const target = document.getElementById('features');
    if (!target) return;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [prefersReducedMotion]);

  return (
    <MarketingLayout>
      <SEO
        title={`${APP_NAME} - Hosting de Servidores de Juegos | Paga solo por jugar`}
        description="Alquila tu propio servidor de Minecraft, Counter-Strike o Terraria. Hardware dedicado, setup en 2 minutos y paga solo por las horas que juegas. Sin suscripciones."
        canonical="https://voltary.org/"
      />

      <motion.div
        className="home"
        initial="hidden"
        animate="show"
      >
        {/* Beta notice */}
        <motion.div className="beta-notice" role="status" variants={fadeIn}>
          <AlertTriangle size={16} strokeWidth={2.25} className="beta-icon" aria-hidden="true" />
          <p>
            Esta plataforma se encuentra en modo <strong>BETA</strong>. Estamos mejorando constantemente.
          </p>
        </motion.div>

        {/* HERO */}
        <section className="home-hero">
          <div className="home-hero-bg" aria-hidden="true">
            <motion.div
              className="home-hero-glow home-hero-glow--1"
              variants={glowFloat(0)}
              animate={{ x: [0, 40, -30, 0], y: [0, -30, 40, 0], scale: [1, 1.15, 0.95, 1] }}
              transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="home-hero-glow home-hero-glow--2"
              variants={glowFloat(0.3)}
              animate={{ x: [0, -35, 45, 0], y: [0, 35, -20, 0], scale: [1, 0.9, 1.2, 1] }}
              transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>

          <motion.div
            className="home-hero-content"
            variants={tightStagger}
            initial="hidden"
            animate="show"
          >
            <motion.span className="home-hero-eyebrow" variants={eyebrow}>
              <Sparkles size={12} /> Hosting de servidores de juego
            </motion.span>

            <motion.h1 className="home-hero-title" variants={heroTitle}>
              {APP_NAME}
            </motion.h1>

            <motion.p className="home-hero-subtitle" variants={heroItem}>
              Hostea tus juegos favoritos y paga <strong>solo por lo que juegas</strong>.
              <br />
              Sin suscripciones, sin compromisos, sin sorpresas.
            </motion.p>

            <motion.div className="home-hero-cta" variants={heroItem}>
              <motion.button
                type="button"
                onClick={handleLogin}
                className="home-discord-btn"
                whileHover={{ y: -3, boxShadow: '0 16px 40px rgba(88, 101, 242, 0.5)' }}
                whileTap={{ scale: 0.96 }}
                transition={{ duration: 0.2 }}
              >
                <DiscordIcon /> Iniciar sesión
                <ArrowRight size={16} style={{ transition: 'transform 0.2s' }} className="home-cta-arrow" />
              </motion.button>
              <motion.a
                href="#games"
                className="home-secondary-btn"
                whileHover={{ y: -3 }}
                whileTap={{ scale: 0.96 }}
                transition={{ duration: 0.2 }}
              >
                Ver juegos disponibles
              </motion.a>
            </motion.div>

            <motion.div className="home-trust" variants={stagger}>
              {trustBadges.map((badge) => (
                <motion.div
                  key={badge.label}
                  className="home-trust-badge"
                  variants={heroItem}
                  whileHover={{ y: -3, color: 'var(--text-primary)', scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  {badge.icon}
                  <span>{badge.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          <motion.button
            type="button"
            onClick={handleScrollToFeatures}
            className="home-scroll-indicator"
            aria-label="Ver más"
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          >
            <span>Descubrir más</span>
            <ChevronDown size={20} />
          </motion.button>
        </section>

        {/* FEATURES */}
        <motion.section
          id="features"
          className="home-section"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-100px' }}
          variants={stagger}
        >
          <motion.div className="home-section-header" variants={sectionReveal}>
            <span className="home-section-tag">CARACTERÍSTICAS</span>
            <h2>¿Por qué elegir {APP_NAME}?</h2>
            <p>La mejor plataforma para hostear tus servidores de juegos</p>
          </motion.div>

          <motion.div className="home-features-grid" variants={stagger}>
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                className="home-feature-card"
                variants={cardItem}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                  borderColor: 'var(--accent)',
                  boxShadow: '0 20px 40px rgba(147, 51, 234, 0.2)',
                }}
                transition={{ duration: 0.25, ease: EASE_OUT }}
              >
                <motion.div
                  className="home-feature-icon"
                  whileHover={{ scale: 1.15, rotate: -6 }}
                  transition={{ duration: 0.3, ease: EASE_BACK }}
                >
                  {feature.icon}
                </motion.div>
                <h3>{feature.title}</h3>
                <p className="home-feature-desc">{feature.description}</p>
                <ul className="home-feature-list">
                  {feature.highlights.map((h) => (
                    <li key={h}>
                      <Check size={12} /> {h}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        {/* SPONSORS */}
        <motion.section
          className="home-sponsors"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-100px' }}
          variants={stagger}
        >
          <motion.div className="home-section-header" variants={sectionReveal}>
            <span className="home-section-tag">PATROCINADORES</span>
            <h2>Apoyado por</h2>
            <p>Gracias a quienes hacen posible este proyecto</p>
          </motion.div>

          <motion.a
            href="https://flexzin.com"
            target="_blank"
            rel="noopener noreferrer"
            className="home-sponsor-card"
            aria-label="Visitar Flexzin"
            variants={cardItem}
            whileHover={{ y: -8, scale: 1.03 }}
            transition={{ duration: 0.3, ease: EASE_OUT }}
          >
            <img src="/Flexzin.webp" alt="Flexzin" />
          </motion.a>
        </motion.section>

        {/* GAMES */}
        <motion.section
          id="games"
          className="home-section home-games"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-100px' }}
          variants={stagger}
        >
          <motion.div className="home-section-header" variants={sectionReveal}>
            <span className="home-section-tag">CATÁLOGO</span>
            <h2>Juegos disponibles</h2>
            <p>Elige tu juego favorito y lanza un servidor en segundos. Más juegos próximamente.</p>
          </motion.div>

          <motion.div className="home-games-grid" variants={stagger}>
            {games.map((game) =>
              game.available ? (
                <motion.div
                  key={game.name}
                  className="home-game-card"
                  variants={cardItem}
                  whileHover={{ y: -8, scale: 1.02, borderColor: 'var(--accent)' }}
                  transition={{ duration: 0.25, ease: EASE_OUT }}
                >
                  <div className="home-game-image">
                    <img src={game.image} alt={game.name} />
                  </div>
                  <div className="home-game-content">
                    <div className="home-game-header">
                      <h3>{game.name}</h3>
                      <span className="home-game-tag">{game.tag}</span>
                    </div>
                    <div className="home-game-meta">
                      <Users size={12} /> {game.players}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={game.name}
                  className="home-game-card home-game-card--coming"
                  variants={cardItem}
                >
                  <div className="home-game-coming-icon">
                    <Clock size={28} />
                  </div>
                  <span className="home-game-name">{game.name}</span>
                  <span className="home-game-coming-label">PRÓXIMAMENTE</span>
                </motion.div>
              )
            )}
          </motion.div>
        </motion.section>

        {/* CTA */}
        <CTABanner
          title="¿Listo para empezar?"
          description="Crea tu primer servidor en menos de 2 minutos y paga solo por el tiempo que lo uses."
          primaryLabel="Crear mi servidor"
        />
      </motion.div>
    </MarketingLayout>
  );
}

// =========================================================================
// Discord icon (logo SVG)
// =========================================================================

function DiscordIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className="home-discord-icon"
      aria-hidden="true"
    >
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}
